import React, {FC, useCallback, useState} from 'react';

import {QuantityStepper} from '@optimaxdev/design-system';
import {connect} from 'react-redux';

import {ApplicationStoreType} from 'desktop/store';
import {isUhcEnabled} from 'libs/rollout';
import {isGlasses} from 'libs/typeCheck/typeCheck';

import {BADGES} from '../../constants/badges';
import {
    changeItemQtyAction,
    ItemChangeQtyType,
} from '../../store/reducers/changeItemQty/changeItemQty';
import {
    getCartItemById,
    isCanChangeItemQty,
    makeGetIsProductHasSecondFrame,
} from '../../store/selectors';
import {CartItemType} from '../../types';

import ActionTooltip from '../actionTooltip/ActionTooltip';

import s from './baseQtySwitcher.scss';

export type PropsType = {
    id: string;
    product: CartItemType;
    isCounterDisabled: boolean;
    setItemQty: (accessoryData: ItemChangeQtyType) => void;
};

/**
 * Class for QTY switcher component, used for glasses and accessories
 * @params {PropsType} props - component props
 * @returns {ReactElement} component
 */
export const BaseQtySwitcher: FC<PropsType> = ({id, isCounterDisabled, product, setItemQty}) => {
    const [isLoading, setIsLoading] = useState(false);

    const MIN_ITEMS = 1;
    const MAX_ITEMS = product.stock?.isLowStock
        ? // stockQty can be more than 2 from BE, but we need to limit it to 2
          Math.min(2, Number(product.stock?.stockQty || 1))
        : 2;

    /**
     * Should set changed qty
     * @param {string} value - qty
     */
    const onQtyChange = useCallback(
        async (value: number) => {
            setIsLoading(true);
            try {
                await setItemQty({qty: value, quoteItemId: id});
            } finally {
                setIsLoading(false);
            }
        },
        [id, setItemQty],
    );

    if (
        (isGlasses(product) && product.badges.some(({code}) => code === BADGES.UPSELLS)) ||
        (product.qty === 1 && isUhcEnabled())
    )
        return null;

    const isLoadingEnabled = isCounterDisabled && isLoading;

    return (
        <div className={s.wrapper}>
            <ActionTooltip qty={product.qty}>
                <span className={s.label}>Quantity:</span>
                <div className={s.quantityStepper}>
                    <QuantityStepper
                        isDisabled={isLoadingEnabled}
                        isLoading={isLoadingEnabled}
                        labelType="none"
                        maxQuantity={MAX_ITEMS}
                        minQuantity={MIN_ITEMS}
                        onChange={onQtyChange}
                        size="extraSmall"
                        value={Number(product.qty)}
                        variant="gray"
                        width={90}
                    />
                </div>
            </ActionTooltip>
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} state - redux store
 * @param {PropsType} props - component own props
 * @returns {PropsType} props
 */
export const mapStateToProps = (state: ApplicationStoreType, props: {id: string}) => {
    const getIsProductHasSecondFrame = makeGetIsProductHasSecondFrame();

    return {
        isCounterDisabled:
            !isCanChangeItemQty(state) || getIsProductHasSecondFrame(state, props.id),
        product: getCartItemById(state, props.id),
    };
};

export const BaseQtySwitcherConnected = connect(mapStateToProps, {
    setItemQty: changeItemQtyAction,
})(BaseQtySwitcher);
